import { apiFetch } from '../http';
export const COPY_REPLACEMENT_WARNING = 'Все текущие данные выбранного путешествия будут удалены и заменены копией. Участники будут удалены из путешествия, существующая гостевая ссылка отключится. Тариф и купленные лимиты сохранятся. Продолжить?';
function isPublicCopy(url, baseUrl) {
    return travelCopyPath(url, baseUrl).startsWith('/travel/api/public_travel/');
}
function importSelection(url, baseUrl, selection) {
    var _a;
    return isPublicCopy(url, baseUrl) ? { ...selection, options: { description: ((_a = selection.options) === null || _a === void 0 ? void 0 : _a.description) !== false } } : selection;
}
export function travelImportDefaultOptions(url, baseUrl, catalog) {
    return isPublicCopy(url, baseUrl) || catalog.source_is_public
        ? { description: Boolean(catalog.available_options.description) }
        : { photos: Boolean(catalog.available_options.photos) };
}
function importQuery(selection = {}) {
    const query = new URLSearchParams({ mode: 'append' });
    for (const [key, value] of Object.entries(selection))
        if (value !== undefined)
            query.set(key, JSON.stringify(value));
    return query;
}
export async function getTravelImportCatalog(auth, url) {
    const catalog = await apiFetch(`${travelCopyPath(url, auth.baseUrl)}?mode=append&catalog=1`, { ...auth, redirectOnUnauthorized: false });
    if (!Array.isArray(catalog.days) || !catalog.available_options)
        throw new Error('Добавление маршрутов сейчас недоступно. Попробуйте позже.');
    if (isPublicCopy(url, auth.baseUrl) || catalog.source_is_public) {
        return { ...catalog, source_is_public: true, available_options: { description: Boolean(catalog.available_options.description) } };
    }
    return catalog;
}
export async function getTravelImportTargets(auth, url, selection) {
    const targets = [];
    while (true) {
        const query = importQuery(importSelection(url, auth.baseUrl, selection));
        query.set('limit', '50');
        query.set('offset', String(targets.length));
        const page = await apiFetch(`${travelCopyPath(url, auth.baseUrl)}?${query}`, { ...auth, redirectOnUnauthorized: false });
        targets.push(...page.results);
        if (!page.next || !page.results.length)
            return targets;
    }
}
export async function checkTravelImport(auth, url, target, selection = {}) {
    const query = importQuery(importSelection(url, auth.baseUrl, selection));
    query.set('target_travel_plan_id', target);
    const check = await apiFetch(`${travelCopyPath(url, auth.baseUrl)}?${query}`, { ...auth, redirectOnUnauthorized: false });
    if (check.mode !== 'append')
        throw new Error('Добавление маршрутов сейчас недоступно. Попробуйте позже.');
    return check;
}
export function travelImportCreation(url, baseUrl, selection, requestId) {
    const path = travelCopyPath(url, baseUrl).split('/');
    return { ...importSelection(url, baseUrl, selection), request_id: requestId, source: path[3] === 'shared_travel' ? { token: path[4] } : { public_id: path[4] } };
}
export function toggleTravelImportDay(catalog, selected, day, checked) {
    var _a;
    const linked = ((_a = catalog.days.find(row => row.date === day)) === null || _a === void 0 ? void 0 : _a.linked_dates) || [day];
    const next = new Set(selected);
    linked.forEach(value => checked ? next.add(value) : next.delete(value));
    return [...next].sort();
}
// Only the advertised copy endpoints are accepted; never send credentials to a foreign URL.
export function travelCopyPath(value, baseUrl) {
    const base = new URL(baseUrl);
    const url = new URL(value, base);
    if (url.origin !== base.origin || !/^\/travel\/api\/(shared_travel|public_travel)\/[^/]+\/copy\/$/.test(url.pathname)) {
        throw new Error('Некорректный адрес копирования путешествия.');
    }
    return url.pathname;
}
export async function getTravelCopyTargets(auth, copyApiUrl) {
    const path = travelCopyPath(copyApiUrl, auth.baseUrl);
    const targets = [];
    let offset = 0;
    while (true) {
        const page = await apiFetch(`${path}?limit=50&offset=${offset}`, { ...auth, redirectOnUnauthorized: false });
        targets.push(...page.results);
        if (!page.next || !page.results.length)
            return targets;
        offset += page.results.length;
    }
}
export function checkTravelCopy(auth, url, target) {
    return apiFetch(`${travelCopyPath(url, auth.baseUrl)}?target_travel_plan_id=${encodeURIComponent(target)}`, {
        ...auth, redirectOnUnauthorized: false,
    });
}
export function executeTravelCopy(auth, url, body) {
    const payload = body.mode === 'append' ? { ...body, ...importSelection(url, auth.baseUrl, body) } : body;
    return apiFetch(travelCopyPath(url, auth.baseUrl), {
        ...auth, method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
        redirectOnUnauthorized: false, requestTimeoutMs: 120000,
    });
}
export const COPY_LIMIT_KINDS = {
    plan_items_limit: 'plan_items', points_limit: 'points', gpx_tracks_limit: 'gpx', file_limit: 'files',
};
/** Shared state machine: approval is never inferred, retries reuse the exact request. */
export function createTravelCopyFlow(deps) {
    let state = { target: '', check: null, busy: false, confirming: false, retrying: false, error: '', result: null };
    let generation = 0;
    let pending = null;
    let selection = {};
    const listeners = new Set();
    const publish = (patch) => { state = { ...state, ...patch }; listeners.forEach(fn => fn()); };
    const select = async (target) => {
        if (state.busy && pending)
            return;
        if (pending && target === state.target)
            return;
        const current = ++generation;
        pending = null;
        const hasSelection = selection.source_dates === undefined || selection.source_dates.length > 0;
        publish({ target, check: null, confirming: false, retrying: false, error: '', result: null, busy: Boolean(target && hasSelection) });
        if (!target || !hasSelection)
            return;
        try {
            const check = await deps.check(target, selection);
            if (current !== generation)
                return;
            if (String(check.target_travel_plan_id) !== target)
                throw new Error('Назначение проверки изменилось. Повторите проверку.');
            publish({ check });
        }
        catch (error) {
            if (current === generation)
                publish({ error: copyErrorMessage(error) });
        }
        finally {
            if (current === generation)
                publish({ busy: false });
        }
    };
    const execute = async (confirmed = false) => {
        var _a, _b;
        if (state.busy || state.result || !((_a = state.check) === null || _a === void 0 ? void 0 : _a.can_copy))
            return;
        if (!pending) {
            if (state.check.requires_confirmation && (!confirmed || !state.confirming)) {
                publish({ confirming: true });
                return;
            }
            if (state.check.requires_confirmation && !state.check.confirmation_token) {
                await select(state.target);
                return;
            }
            pending = { target_travel_plan_id: state.target, request_id: deps.uuid(),
                ...(state.check.mode === 'append' ? { mode: 'append', source_dates: state.check.source_dates,
                    options: selection.options, date_mapping: state.check.date_mapping, preview_token: state.check.preview_token } : {}),
                ...(state.check.requires_confirmation ? { confirm_replace: true, confirmation_token: state.check.confirmation_token } : {}) };
        }
        publish({ busy: true, confirming: false, error: '' });
        try {
            const result = await deps.copy(pending);
            if (!result.success)
                throw new Error(result.message || 'Не удалось скопировать путешествие.');
            pending = null;
            publish({ result, retrying: false });
        }
        catch (error) {
            const check = (_b = error === null || error === void 0 ? void 0 : error.data) === null || _b === void 0 ? void 0 : _b.copy_check;
            const definitive = (error === null || error === void 0 ? void 0 : error.status) >= 400 && (error === null || error === void 0 ? void 0 : error.status) < 500;
            if (definitive)
                pending = null;
            publish({ error: copyErrorMessage(error), retrying: Boolean(pending),
                ...(definitive ? { check: check && String(check.target_travel_plan_id) === state.target ? check : null } : {}),
                confirming: Boolean((check === null || check === void 0 ? void 0 : check.can_copy) && (check === null || check === void 0 ? void 0 : check.requires_confirmation)),
            });
        }
        finally {
            publish({ busy: false });
        }
    };
    return {
        getSnapshot: () => state,
        subscribe: (fn) => { listeners.add(fn); return () => { listeners.delete(fn); }; },
        select, execute,
        configure: async (next) => {
            if (pending)
                return;
            selection = next;
            await select(state.target);
        },
        refresh: () => pending ? Promise.resolve() : select(state.target),
        cancelConfirmation: () => publish({ confirming: false }),
    };
}
export function copyErrorMessage(error) {
    const data = error === null || error === void 0 ? void 0 : error.data;
    if (typeof (data === null || data === void 0 ? void 0 : data.error) === 'string')
        return data.error;
    if (data === null || data === void 0 ? void 0 : data.errors)
        return typeof data.errors === 'string' ? data.errors : Object.values(data.errors).flat().join(', ');
    if ((error === null || error === void 0 ? void 0 : error.status) === 423)
        return 'Путешествие сейчас изменяется. Повторите попытку позже.';
    if ((error === null || error === void 0 ? void 0 : error.status) === 404)
        return 'Источник недоступен или выбранное путешествие вам не принадлежит.';
    return (error === null || error === void 0 ? void 0 : error.message) || 'Не удалось выполнить запрос. Проверьте подключение и повторите попытку.';
}
export function copiedStatisticsRows(value) {
    var _a, _b;
    const overall = ((_a = value === null || value === void 0 ? void 0 : value.statistics) === null || _a === void 0 ? void 0 : _a.overall) || {};
    const tracks = ((_b = value === null || value === void 0 ? void 0 : value.statistics) === null || _b === void 0 ? void 0 : _b.tracks) || {};
    return [['Стран', overall.countries_count], ['Участников', overall.participants_count],
        ['Пунктов', overall.items_count], ['Точек', overall.points_count], ['Треков', tracks.tracks_count],
        ['Дистанция, км', tracks.total_distance], ['Начало', overall.date_start], ['Окончание', overall.date_finish]]
        .filter(([, v]) => v !== undefined && v !== null).map(([label, v]) => ({ label: String(label), value: String(v) }));
}
